// =============================================================================
// ModifierCard — one combat modifier in the campaign Combat tab
// =============================================================================
import { useState } from 'react';
import { ChevronDown, Pencil, Trash2, ToggleLeft, ToggleRight, User } from 'lucide-react';
import type { CombatModifierWithStates } from '../../../../backend/shared/types/index';
import ModifierEffectRow from './ModifierEffectRow';

interface CharacterRow {
  characterId: string;
  name:        string;
}

interface Props {
  modifier:       CombatModifierWithStates;
  bonusTypeNames: Record<string, string>;  // typeId → display name
  characters:     CharacterRow[];          // party characters for per-char toggles
  isOwnerOrAdmin: boolean;
  /** Current user's ID — allows the modifier's creator to edit/delete their own modifiers. */
  currentUserId?: string;
  onToggleActive:   (modifierId: string, active: boolean) => void;
  onToggleCharacter:(modifierId: string, charId: string, enabled: boolean) => void;
  onEdit:          (modifier: CombatModifierWithStates) => void;
  onDelete:        (modifierId: string) => Promise<void>;
}

export default function ModifierCard({
  modifier, bonusTypeNames, characters, isOwnerOrAdmin, currentUserId,
  onToggleActive, onToggleCharacter, onEdit, onDelete,
}: Props) {
  const [expanded,         setExpanded]         = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting,         setDeleting]         = useState(false);

  // Can edit/delete: DM/admin OR the modifier's own creator
  const canEdit = isOwnerOrAdmin || (!!currentUserId && modifier.createdBy === currentUserId);

  const isActiveForChar = (charId: string) => {
    if (!modifier.isActive) return false;
    const override = modifier.characterStates[charId];
    return override !== undefined ? override : modifier.defaultEnabled;
  };

  const activeChars = characters.filter((c) => isActiveForChar(c.characterId)).length;

  return (
    <div className={`bg-darkbg-card border rounded-lg overflow-hidden transition-colors ${
      modifier.isActive ? 'border-darkbg-border' : 'border-darkbg-border/40 opacity-60'
    }`}>
      {/* ── Header — entire row toggles expand; inner buttons stop propagation ── */}
      <div
        onClick={() => !confirmingDelete && setExpanded((v) => !v)}
        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
          confirmingDelete ? '' : 'cursor-pointer hover:bg-darkbg-tertiary/40'
        }`}
      >
        {/* Active master toggle */}
        {isOwnerOrAdmin ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleActive(modifier.modifierId, !modifier.isActive); }}
            className="shrink-0 text-parchment-muted/60 hover:text-gold transition-colors"
            title={modifier.isActive ? 'Deactivate' : 'Activate'}
          >
            {modifier.isActive
              ? <ToggleRight className="w-5 h-5 text-success" />
              : <ToggleLeft  className="w-5 h-5" />}
          </button>
        ) : (
          <div className={`w-2 h-2 rounded-full shrink-0 ${modifier.isActive ? 'bg-success' : 'bg-darkbg-tertiary'}`} />
        )}

        {/* Name + source */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="font-display text-sm text-parchment truncate">{modifier.name}</p>
            {/* Owner type badge */}
            {(modifier as any).ownerType === 'self-only' ? (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400/70 border border-purple-500/20 shrink-0 whitespace-nowrap" title="Personal ability — only you see this">
                <User className="w-2.5 h-2.5 inline mr-0.5" />Self-Only
              </span>
            ) : (modifier as any).ownerType === 'character' ? (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400/70 border border-indigo-500/20 shrink-0 whitespace-nowrap">
                {(modifier as any).ownerCharacterName ?? 'Character-Specific'}
              </span>
            ) : (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-darkbg-tertiary text-parchment-muted/40 border border-darkbg-border shrink-0">
                Party-Wide
              </span>
            )}
            {modifier.worksInAntimagic && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300/70 border border-emerald-500/20 shrink-0 whitespace-nowrap" title="Still applies in Antimagic or Dead Magic">
                Antimagic
              </span>
            )}
          </div>
          <p className="text-xs text-parchment-muted/50 truncate">
            {modifier.sourceName}
            {modifier.isActive && characters.length > 0 && (
              <span className="ml-1.5 text-gold/60">
                · {activeChars}/{characters.length} chars
              </span>
            )}
          </p>
        </div>

        {/* Effects summary pill */}
        <span className="shrink-0 text-xs text-parchment-muted/40 tabular-nums">
          {modifier.effects.length} {modifier.effects.length === 1 ? 'effect' : 'effects'}
        </span>

        {/* Actions — edit + delete (hidden while confirming delete) */}
        {canEdit && !confirmingDelete && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(modifier); }}
              className="p-1 rounded text-parchment-muted/40 hover:text-gold hover:bg-gold/10 transition-colors"
              title="Edit modifier"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
              className="p-1 rounded text-parchment-muted/40 hover:text-danger hover:bg-danger/10 transition-colors"
              title="Delete modifier"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Inline delete confirmation */}
        {canEdit && confirmingDelete && (
          <div className="flex items-center gap-2 shrink-0 border-l border-danger/30 pl-3"
               onClick={(e) => e.stopPropagation()}>
            <span className="text-parchment-muted text-xs whitespace-nowrap">Delete?</span>
            <button
              onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); }}
              disabled={deleting}
              className="px-2 py-0.5 text-xs text-parchment-muted border border-darkbg-border rounded
                         hover:border-parchment-muted/50 transition-colors disabled:opacity-50"
            >
              No
            </button>
            <button
              disabled={deleting}
              onClick={async (e) => {
                e.stopPropagation();
                setDeleting(true);
                await onDelete(modifier.modifierId);
                setDeleting(false);
                setConfirmingDelete(false);
              }}
              className="px-2 py-0.5 text-xs text-white bg-danger/80 hover:bg-danger rounded
                         transition-colors disabled:opacity-60"
            >
              {deleting ? '…' : 'Yes'}
            </button>
          </div>
        )}

        {/* Expand chevron — visual indicator only; entire row handles the click */}
        {!confirmingDelete && (
          <ChevronDown className={`w-4 h-4 text-parchment-muted/30 shrink-0 transition-transform duration-150 ${
            expanded ? 'rotate-180' : ''
          }`} />
        )}
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="border-t border-darkbg-border/60 px-4 py-3 space-y-3">
          {/* Effects list */}
          <div className="space-y-1">
            {modifier.effects.map((effect) => (
              <ModifierEffectRow
                key={effect.effectId}
                effect={effect}
                bonusTypeName={effect.bonusTypeId ? (bonusTypeNames[effect.bonusTypeId] ?? effect.bonusTypeId) : ''}
              />
            ))}
          </div>

          {/* Per-character toggles (hidden for self-only modifiers) */}
          {characters.length > 0 && modifier.isActive && (modifier as any).ownerType !== 'self-only' && (
            <div className="space-y-1 pt-1 border-t border-darkbg-border/40">
              <p className="text-[10px] uppercase tracking-widest text-parchment-muted/40 mb-1.5">
                Active for characters
              </p>
              {characters.map((char) => {
                const active = isActiveForChar(char.characterId);
                return (
                  <div key={char.characterId} className="flex items-center gap-2">
                    <User className="w-3 h-3 text-parchment-muted/30 shrink-0" />
                    <span className="flex-1 text-xs text-parchment/70 truncate">{char.name}</span>
                    {isOwnerOrAdmin ? (
                      <button
                        onClick={() => onToggleCharacter(modifier.modifierId, char.characterId, !active)}
                        className="shrink-0"
                        title={active ? 'Disable for this character' : 'Enable for this character'}
                      >
                        {active
                          ? <ToggleRight className="w-4 h-4 text-success" />
                          : <ToggleLeft  className="w-4 h-4 text-parchment-muted/40" />}
                      </button>
                    ) : (
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${active ? 'bg-success' : 'bg-darkbg-tertiary'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
